import React from 'react'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { Navigate, Route, Routes } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { persistor, store } from '@core/Store'
import { queryClient } from '@core/Data/ReactQuery'
import { AppRoutes } from './AppRoutes'
import { Home } from './Areas/Home/Home'
import { Jackpots } from './Areas/Jackpots'
import { MeetingsPage } from './Areas/Meetings'
import { NextEventsPage } from './Areas/NextEvents/NextEventsPage'
import { FavouriteNumbersPage } from './Areas/FavouriteNumbers/FavouriteNumbersPage'
import { SkyVideoPlayer } from './Areas/SkyVideoPlayer'
import { FloatingTabRadioButton } from './Areas/Navigation/Components/FloatingTabRadioButton'
import { StructuredSearchPage } from './Areas/StructuredSearch'
import { AppHeader } from './Areas/AppHeader/AppHeader'
import { RaceListPage } from './Areas/RaceList/RaceList'
import { RaceEvents } from './Areas/Racing/Components/RaceEvents/RaceEvents'
import { Withdraw } from './Areas/Withdraw'
import { ContactUsPage } from './Areas/ContactUs/ContactUsPage'
import { BankAccountEdit } from './Areas/BankAccount'
import { SpecialsPage } from './Areas/Specials/SpecialsPage'
import { CurrentPoolList, Games, ResultedPoolList, TippingPage } from './Areas/Tipping'
import { Blackbook } from './Areas/Blackbook'
import { AccountActivity } from './Areas/AccountActivity'
import { ReverificationPopupMobi } from './Areas/Reverification/ReverificationPopup'
import { KambiClient, KambiRouteEffects, useKambiClient } from './Areas/KambiClient'
import { Overlay } from '@core/Components/Overlay/Overlay'
import { GlobalCss } from './Settings/Components/GlobalCss'
import { MyPreferences } from './Areas/Account/MyPreferences'
import { MysteryBetPage } from './Areas/MysteryBet'
import { SignUp } from './Areas/Account/SignUp'
import { Toolbox, DevTools } from './Areas/Toolbox'
import { ChangePassword, ChangeTemporaryPassword } from './Areas/ChangePassword'
import { RequireAuth } from '@core/Components/RequireAuth'
import { ContactDetails, ContactDetailsEditMobiWrapper } from './Areas/ContactDetails'
import { CloseAccount } from './Areas/CloseAccount'
import { BiometricsPage } from './Areas/Biometrics/BiometricsPage'
import { ModalContainer } from './Components/Modal'
import { useAndroidBackButton } from '@core/NativeServices/Navigation/useAndroidBackButton'
import { Account, BetOfferPage, TakeABreakPage, Logout } from './Areas/Account'
import { ActivityStatements } from './Areas/Account/ActivityStatements'
import { TABLocatorWeb } from './Areas/TABLocatorWeb'
import { RaceCardFixedOnly } from './Areas/RaceCardFixedOnly'
import { Login } from './Areas/Login'
import { useInitAnalyticsDataLayer } from './Services/Analytics'
import { RaceCard } from './Areas/RaceCard'
import { RaceCardSky } from './Areas/RaceCardSky'
import { GoodThingsPage } from './Areas/Racing/Components/RacingForm/GoodThingsPage'
import { Settings } from './Areas/Settings'
import { Deposit } from './Areas/Deposit'
import { useOptimoveTrackingEvents } from './Services/Optimove/hooks'
import { useNativeAppLaunchState } from './NativeServices/AppLaunchState'
import { ForceNativeAppUpdate } from './NativeServices/ForceAppUpdate'
import { TabNavigation } from './Areas/Navigation/Components/TabNavigation'
import { Popup as PopupLegacy } from '@core/Components/Popup/Popup'
import { HostContextMobile } from '@core/Contexts/HostContextMobile'
import { BetSlip } from './Areas/BetSlip'
import { Toasts } from '@mobi/component-library/Feedback/Toast'
import { DepositLimits } from './Areas/DepositLimits'
import { useNavigationObserver } from './State/Navigation/hooks/useNavigationObserver'
import { AppRouter } from './Utils/routing/AppRouter'
import { appHistory } from './Utils/routing/history'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import {
  CONTACT_DETAILS_ROOT_ROUTE,
  routes as contactDetailsRoutes,
} from '@mobi/account/Areas/ContactDetails/Utils'
import { BetLoopRoot } from './Areas/BetLoop'
import {
  BetLoopMyProfilePage,
  BetLoopFeedPage,
  BetLoopProfilePage,
  BetLoopSearchProfilesPage,
} from './Areas/BetLoop/Pages'
import { ShareBetOutgoing } from './Areas/ShareBet/ShareBetOutgoing'
import { MFASettingsPages } from './Areas/Settings/Components/MFASettings/Pages'
import { ShortenedUrlHandler } from './Areas/ShortenedUrls'
import { QualtricsBootstrap } from './Areas/Qualtrics/QualtricsBootstrap'
import { ProfileFollowListPage } from './Areas/BetLoop/Pages/ProfileFollowList'
import { MyProfileFollowListPage } from './Areas/BetLoop/Pages/MyProfileFollowList'
import { TrustedDeviceTray } from '@core/Areas/Login/Components/MFALogin/TrustedDeviceTray'
import { useQualtricsInterceptor } from './Areas/Qualtrics/qualtrics'
import { VenueMode } from '@core/Areas/VenueMode/VenueMode'
import { ContentErrorBoundary } from './Components/ErrorBoundary/ContentErrorBoundary'
import { BlockDebitCardPage } from '@mobi/account/Areas/BlockDebitCard/BlockDebitCardPage'
import { BetLimitPage } from '@mobi/account/Areas/BetLimit/BetLimitPage'
import { CameraPermissionModal } from '@core/Components/CameraPermission'
import { SportOfferingRoot } from './Areas/KambiSports/Offering/Root'
import { PersonalDetails } from './Areas/Account/PersonalDetails'
import { routes as accountRoutes } from '@mobi/account/Utils'

const App = () => (
  <Provider store={store}>
    <PersistGate persistor={persistor} loading={null}>
      <QueryClientProvider client={queryClient}>
        <AppRouter history={appHistory}>
          <HostContextMobile>
            <AppWithRoutes />
          </HostContextMobile>
        </AppRouter>
      </QueryClientProvider>
    </PersistGate>
  </Provider>
)

const AppWithRoutes = () => {
  useNavigationObserver()
  useKambiClient()
  useAndroidBackButton()
  useInitAnalyticsDataLayer()
  useOptimoveTrackingEvents()
  useNativeAppLaunchState()
  useQualtricsInterceptor()

  return (
    <>
      <AppHeader />

      <Login />
      <Overlay />
      <Toasts />

      <TrustedDeviceTray />
      <ForceNativeAppUpdate />

      <ModalContainer />

      <ShareBetOutgoing />

      <FloatingTabRadioButton />
      <DevTools />

      <ReverificationPopupMobi />
      <PopupLegacy />
      <CameraPermissionModal />

      <GlobalCss />

      <SkyVideoPlayer />

      <BetSlip />

      <div id='js-react-app-routes' className='mobi__z-stack-content'>
        <ContentErrorBoundary>
          <Routes>
            <Route path={AppRoutes.Home} element={<Home />} />

            <Route path={AppRoutes.SignUp + '/*'} element={<SignUp />} />

            <Route path={AppRoutes.RaceCard} element={<RaceCard />} />

            <Route path={AppRoutes.RaceCardSky} element={<RaceCardSky />} />

            <Route path={AppRoutes.Jackpots} element={<Jackpots />} />

            {AppRoutes.NextEvents.map(x => (
              <Route key={x} path={x} element={<NextEventsPage />} />
            ))}

            <Route path={AppRoutes.SportsRoot + '/*'} element={<KambiRouteEffects />} />

            <Route path={AppRoutes.Sports + '/*'} element={<SportOfferingRoot />} />

            <Route path={AppRoutes.Meetings} element={<MeetingsPage />} />

            <Route
              path={AppRoutes.Account}
              element={
                <RequireAuth>
                  <Account />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.ChangePassword}
              element={
                <RequireAuth>
                  <ChangePassword />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ChangeTemporaryPassword} element={<ChangeTemporaryPassword />} />

            <Route path={AppRoutes.MarketingCommunications}>
              <Route
                index
                element={
                  <RequireAuth>
                    <MyPreferences />
                  </RequireAuth>
                }
              />

              <Route path='optin' element={<MyPreferences shouldAutoOptIn />} />
            </Route>

            <Route path={AppRoutes.CloseAccount} element={<CloseAccount />} />

            <Route path={AppRoutes.FavouriteNumbers} element={<FavouriteNumbersPage />} />

            <Route path={AppRoutes.Biometrics} element={<BiometricsPage />} />

            <Route
              path='/favouritenumbers'
              element={<Navigate to='/favourite-numbers' replace />}
            />

            {AppRoutes.AccountActivity.map(x => (
              <Route
                key={x}
                path={x}
                element={
                  <RequireAuth>
                    <AccountActivity />
                  </RequireAuth>
                }
              />
            ))}

            <Route path={AppRoutes.StructuredSearch} element={<StructuredSearchPage />} />

            <Route
              path={contactDetailsRoutes.editAddress}
              element={
                <RequireAuth>
                  <ContactDetailsEditMobiWrapper />
                </RequireAuth>
              }
            />

            <Route
              path={CONTACT_DETAILS_ROOT_ROUTE + '/*'}
              element={
                <RequireAuth>
                  <ContactDetails />
                </RequireAuth>
              }
            />

            <Route
              path={accountRoutes.personalDetails}
              element={
                <RequireAuth>
                  <PersonalDetails />
                </RequireAuth>
              }
            />

            <Route
              path={WITHDRAW_ROOT_ROUTE + '/*'}
              element={
                <RequireAuth>
                  <Withdraw />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ContactUs} element={<ContactUsPage />} />

            <Route
              path={AppRoutes.BankAccountEdit + '/*'}
              element={
                <RequireAuth>
                  <BankAccountEdit />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Precommitment}
              element={
                <RequireAuth>
                  <BetLimitPage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.TakeABreak}
              element={
                <RequireAuth>
                  <TakeABreakPage />
                </RequireAuth>
              }
            />

            {AppRoutes.MysteryBet.map(x => (
              <Route key={x} path={x} element={<MysteryBetPage />} />
            ))}

            <Route path={AppRoutes.GoodThings} element={<GoodThingsPage />} />

            <Route
              path={AppRoutes.Specials + '/*'}
              element={
                <RequireAuth>
                  <SpecialsPage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.ActivityStatements}
              element={
                <RequireAuth>
                  <ActivityStatements />
                </RequireAuth>
              }
            />

            {AppRoutes.RaceList.map(x => (
              <Route key={x} path={x} element={<RaceListPage />} />
            ))}

            <Route path={AppRoutes.RaceEvents} element={<RaceEvents />} />

            <Route path={AppRoutes.RaceCardFixedOnly} element={<RaceCardFixedOnly />} />

            <Route path={AppRoutes.Tipping} element={<TippingPage />}>
              <Route index element={<CurrentPoolList />} />
              <Route path=':sportIconCode/:poolKey' element={<Games />} />
              <Route path='results/:sportIconCode/:poolKey' element={<Games />} />
              <Route path='results' element={<ResultedPoolList />} />
              <Route path='*' element={<Navigate to={AppRoutes.Tipping} />} />
            </Route>

            <Route
              path={AppRoutes.Blackbook}
              element={
                <RequireAuth>
                  <Blackbook />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Offers}
              element={
                <RequireAuth>
                  <BetOfferPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.VenueMode} element={<VenueMode />} />

            <Route path={AppRoutes.TABLocatorWeb} element={<TABLocatorWeb />} />

            <Route path={AppRoutes.Toolbox} element={<Toolbox />} />

            <Route path={AppRoutes.AccountLogout} element={<Logout />} />

            <Route
              path={AppRoutes.Settings}
              element={
                <RequireAuth>
                  <Settings />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.SettingsMFA + '/*'}
              element={
                <RequireAuth>
                  <MFASettingsPages />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Deposit + '/*'}
              element={
                <RequireAuth>
                  <Deposit flow='account-deposit' />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.DepositLimits}
              element={
                <RequireAuth>
                  <DepositLimits />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.BetLoop} element={<BetLoopRoot />}>
              <Route index element={<BetLoopFeedPage />} />
              <Route path='my-profile' element={<BetLoopMyProfilePage />} />
              <Route path='my-profile/follow-list' element={<MyProfileFollowListPage />} />
              <Route path='profile/:profileId' element={<BetLoopProfilePage />} />
              <Route path='profile/:profileId/follow-list' element={<ProfileFollowListPage />} />
              <Route path='search' element={<BetLoopSearchProfilesPage />} />
              <Route path='*' element={<Navigate to={AppRoutes.BetLoop} />} />
            </Route>

            <Route
              path={AppRoutes.BlockDebitCard}
              element={
                <RequireAuth>
                  <BlockDebitCardPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ShortenedUrls} element={<ShortenedUrlHandler />} />

            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </ContentErrorBoundary>
      </div>

      <KambiClient key='kambi-client' />
      <QualtricsBootstrap />
      <TabNavigation />
    </>
  )
}

export default App
